import React, { useState, useEffect, useRef, useCallback } from 'react';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useTheme, alpha } from '@mui/material/styles';
import ThumbsUpIcon from '../../../NRIcons/ThumbsUpIcon';
import ThumbsUpIconFilled from '../../../NRIcons/ThumbsUpIconFilled';
import ThumbsDownIcon from '../../../NRIcons/ThumbsDownIcon';
import ThumbsDownIconFilled from '../../../NRIcons/ThumbsDownIconFilled';

export default function FeedbackActions({
  feedback,
  onFeedbackSubmit,
  onFeedbackDelete,
  sessionId,
  eventId,
  onFormStateChange
}) {
  const theme = useTheme();
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const prevIsThumbsDownRef = useRef(false);
  const [optimisticThumbsUp, setOptimisticThumbsUp] = useState(null);
  const [optimisticThumbsDown, setOptimisticThumbsDown] = useState(null);
  const prevFeedbackIdRef = useRef(feedback?.id);

  const currentRating = feedback?.rating;
  
  // Use optimistic state if available, otherwise use feedback state
  // They are mutually exclusive - if one is true, the other must be false
  const isThumbsUp = optimisticThumbsUp !== null 
    ? optimisticThumbsUp 
    : (optimisticThumbsDown === true ? false : (currentRating === 'thumbs_up'));
  const isThumbsDown = optimisticThumbsDown !== null 
    ? optimisticThumbsDown 
    : (optimisticThumbsUp === true ? false : (currentRating === 'thumbs_down'));

  // Close form if feedback changes from thumbs_down to something else (thumbs_up or deleted)
  useEffect(() => {
    const wasThumbsDown = prevIsThumbsDownRef.current;
    const isNowThumbsDown = isThumbsDown;
    
    // Only close if it was thumbs_down before and is no longer thumbs_down
    if (wasThumbsDown && !isNowThumbsDown && showFeedbackForm) {
      setShowFeedbackForm(false);
    }
    
    // Update the ref for next comparison
    prevIsThumbsDownRef.current = isNowThumbsDown;
  }, [isThumbsDown, showFeedbackForm]);

  // Reset optimistic state when feedback updates from backend (after API call completes)
  useEffect(() => {
    // Only reset if feedback actually changed (not just initial load)
    if (prevFeedbackIdRef.current !== feedback?.id) {
      setOptimisticThumbsUp(null);
      setOptimisticThumbsDown(null);
      prevFeedbackIdRef.current = feedback?.id;
    }
  }, [feedback?.id, feedback?.rating]);

  const handleThumbsUp = async () => {
    const currentIsThumbsUp = optimisticThumbsUp !== null ? optimisticThumbsUp : (currentRating === 'thumbs_up');
    
    if (currentIsThumbsUp) {
      setOptimisticThumbsUp(false);
      setOptimisticThumbsDown(null);
      if (onFeedbackDelete && sessionId && eventId) {
        try {
          await onFeedbackDelete(sessionId, eventId);
        } catch (error) {
          setOptimisticThumbsUp(true);
        }
      }
    } else {
      setOptimisticThumbsUp(true);
      setOptimisticThumbsDown(false);
      if (onFeedbackSubmit && sessionId && eventId) {
        try {
          await onFeedbackSubmit(sessionId, eventId, 'thumbs_up', '');
        } catch (error) {
          setOptimisticThumbsUp(false);
          setOptimisticThumbsDown(null);
        }
      }
      setShowFeedbackForm(false);
    }
  };

  const handleThumbsDown = async () => {
    const currentIsThumbsDown = optimisticThumbsDown !== null ? optimisticThumbsDown : (currentRating === 'thumbs_down');
    
    if (currentIsThumbsDown) {
      setOptimisticThumbsDown(false);
      setOptimisticThumbsUp(null);
      if (onFeedbackDelete && sessionId && eventId) {
        try {
          await onFeedbackDelete(sessionId, eventId);
        } catch (error) {
          setOptimisticThumbsDown(true);
        }
        setShowFeedbackForm(false);
      }
    } else {
      setOptimisticThumbsDown(true);
      setOptimisticThumbsUp(false);
      setShowFeedbackForm(true);
    }
  };

  // Expose handlers for form submission/cancellation - wrapped in useCallback to prevent infinite loops
  const handleFeedbackSubmit = useCallback(async (reason) => {
    if (onFeedbackSubmit && sessionId && eventId) {
      try {
        await onFeedbackSubmit(sessionId, eventId, 'thumbs_down', reason);
        setShowFeedbackForm(false);
      } catch (error) {
        setOptimisticThumbsDown(false);
        setOptimisticThumbsUp(null);
      }
    }
  }, [onFeedbackSubmit, sessionId, eventId]);

  const handleFeedbackCancel = useCallback(() => {
    setShowFeedbackForm(false);
    setOptimisticThumbsDown(null);
    setOptimisticThumbsUp(null);
  }, []);

  const onFormStateChangeRef = useRef(onFormStateChange);
  useEffect(() => {
    onFormStateChangeRef.current = onFormStateChange;
  }, [onFormStateChange]);

  useEffect(() => {
    if (onFormStateChangeRef.current) {
      onFormStateChangeRef.current(showFeedbackForm, {
        handleFeedbackSubmit,
        handleFeedbackCancel
      });
    }
  }, [showFeedbackForm, handleFeedbackSubmit, handleFeedbackCancel]);

  return (
    <>
      <Tooltip title={isThumbsUp ? 'Remove thumbs up' : 'Thumbs up'}>
        <IconButton
          size="small"
          onClick={handleThumbsUp}
          sx={{
            p: 0.5,
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: alpha(theme.palette.text.secondary, 0.1),
              color: theme.palette.text.secondary
            }
          }}
        >
          {isThumbsUp ? (
            <ThumbsUpIconFilled 
              width={20} 
              height={20}
              color={theme.palette.text.icon}
            />
          ) : (
            <ThumbsUpIcon 
              width={20} 
              height={20}
              color={theme.palette.text.icon}
            />
          )}
        </IconButton>
      </Tooltip>
      <Tooltip title={isThumbsDown ? 'Remove thumbs down' : 'Thumbs down'}>
        <IconButton
          size="small"
          onClick={handleThumbsDown}
          sx={{
            p: 0.5,
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: alpha(theme.palette.text.secondary, 0.1),
              color: theme.palette.text.secondary
            }
          }}
        >
          {isThumbsDown ? (
            <ThumbsDownIconFilled 
              width={20} 
              height={20}
              color={theme.palette.text.icon}
            />
          ) : (
            <ThumbsDownIcon 
              width={20} 
              height={20}
              color={theme.palette.text.icon}
            />
          )}
        </IconButton>
      </Tooltip>
    </>
  );
}

